import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { TransactionFilter, PagedMerchantTransactionResult } from '../models/merchant-transaction.model';
import { WalletBalances, CurrencyNetworkResponse } from '../models/wallet.model';
import { ToggleSweepingRequestDto } from './merchant.service';
import { AUTH_CONFIG, type AuthConfig } from '@olv/ui-shared/features/auth';
import { SystemHealthPayload } from './signalr.service';
import { Resource } from '../models/settlement-permissions.model';
import { BlockBounds, ExchangeRateResponseDto, SystemConfiguration } from '../models/system.model';

@Injectable({
  providedIn: 'root',
})
export class SystemService {
  private http = inject(HttpClient);

  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);

  private readonly apiUrl = `${this.cfg.baseUrl}/api/SystemDashboard`;

  dashboardRefreshSignal: WritableSignal<number> = signal(0);

  triggerDashboardRefresh(): void {
    this.dashboardRefreshSignal.update((v) => v + 1);
  }

  getSystemBalance(includeMerchants: boolean): Observable<WalletBalances> {
    const url = `${this.apiUrl}/system-balance`;
    const params = new HttpParams().set('includeMerchants', includeMerchants);
    return this.http.get<WalletBalances>(url, { params });
  }

  /**
   * Get transaction history with pagination
   * @param filter Optional filter parameters
   */
  getSystemTransactions(filter?: TransactionFilter): Observable<PagedMerchantTransactionResult> {
    const url = `${this.cfg.baseUrl}/api/SystemTransaction`;
    let params = new HttpParams();

    if (filter) {
      if (filter.skipCount !== undefined) {
        params = params.set('SkipCount', filter.skipCount.toString());
      }

      if (filter.maxResultCount !== undefined) {
        params = params.set('MaxResultCount', filter.maxResultCount.toString());
      }

      if (filter.filter) {
        params = params.set('Filter', filter.filter);
      }

      if (filter.sorting) {
        params = params.set('Sorting', filter.sorting);
      }

      if (filter.account) {
        params = params.set('Account', filter.account);
      }

      if (filter.actionRequired !== undefined) {
        filter.actionRequired.forEach((action) => (params = params.append('ActionRequired', action.toString())));
      }
      if (filter.transactionType?.length) {
        filter.transactionType.forEach((type) => {
          if (type !== 6) {
            params = params.append('TransactionType', type.toString());
          }
        });
      }

      if (filter.tradeType?.length) {
        filter.tradeType.forEach((type) => (params = params.append('TradeType', type.toString())));
      }

      if (filter.status?.length) {
        filter.status.forEach((status) => (params = params.append('TransactionStatus', status.toString())));
      }

      if (filter.blockchain?.length) {
        filter.blockchain.forEach((network) => (params = params.append('Blockchain', network.toString())));
      }

      if (filter.riskLevel?.length) {
        filter.riskLevel.forEach((level) => (params = params.append('RiskLevel', level.toString())));
      }

      if (filter.exchangeCategories?.length) {
        filter.exchangeCategories.forEach((category) => (params = params.append('ExchangeCategories', category)));
      }

      // Tri-state: undefined applies no restriction, so false has to be sent explicitly rather than skipped.
      if (filter.exchangeRateOverridden !== undefined) {
        params = params.set('ExchangeRateOverridden', filter.exchangeRateOverridden.toString());
      }
    }

    return this.http.get<PagedMerchantTransactionResult>(url, { params }).pipe(
      map((response) => {
        const transformedItems = response.items.map((item) => ({
          ...item,
          date: new Date(item.date),
        }));

        return {
          ...response,
          items: transformedItems,
        };
      }),
      catchError(
        this.handleError<PagedMerchantTransactionResult>('getMerchantTransactions', {
          totalCount: 0,
          items: [],
        }),
      ),
    );
  }

  getSystemConfiguration(): Observable<SystemConfiguration> {
    return this.http.get<SystemConfiguration>(`${this.apiUrl}/system-configuration`);
  }

  updateSystemSweepingStatus(isSweepingEnabled: boolean, totp: string): Observable<void> {
    const url = isSweepingEnabled ? `${this.apiUrl}/disable-sweeping` : `${this.apiUrl}/enable-sweeping`;

    const body: ToggleSweepingRequestDto = {
      totp: totp,
    };

    return this.http.put<void>(url, body).pipe(
      catchError((error) => {
        const message = `Failed to update sweeping status for system: ${error.message}`;
        console.error(message);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get currency based on network type
   * @param networkType
   * @returns Observable of CurrencyNetworkResponse array
   */
  getCurrencyNetwork(networkType: number): Observable<CurrencyNetworkResponse[]> {
    const url = `${this.cfg.baseUrl}/api/Currency/network/${networkType}`;
    return this.http
      .get<CurrencyNetworkResponse[]>(url)
      .pipe(catchError(this.handleError<CurrencyNetworkResponse[]>('getCurrencyNetwork', [])));
  }

  getHealthSnapshot(): Observable<SystemHealthPayload | null> {
    const url = `${this.cfg.baseUrl}/api/HealthStatus/snapshot`;
    return this.http
      .get<SystemHealthPayload>(url)
      .pipe(catchError(this.handleError<SystemHealthPayload | null>('getHealthSnapshot', null)));
  }

  getExchangeRates(): Observable<ExchangeRateResponseDto> {
    const url = `${this.cfg.baseUrl}/api/Currency/exchange-rates`;

    return this.http.get<ExchangeRateResponseDto>(url).pipe(
      catchError(
        this.handleError<ExchangeRateResponseDto>('getExchangeRates', {
          baseCurrency: '',
          baseCurrencySymbol: '',
          baseCurrencyDisplayPrecision: 0,
          exchangeRates: [],
        }),
      ),
    );
  }

  getBlockBounds(resource: Resource): Observable<BlockBounds> {
    return this.http.get<BlockBounds>(`${this.cfg.baseUrl}/api/${resource}/block-bounds`);
  }

  processBlock(resource: Resource, blockNumber: number): Observable<void> {
    return this.http.post<void>(`${this.cfg.baseUrl}/api/${resource}/process-block`, { blockNumber });
  }

  processBlockRange(resource: Resource, fromBlockNumber: number, toBlockNumber: number): Observable<void> {
    return this.http.post<void>(`${this.cfg.baseUrl}/api/${resource}/process-block-range`, {
      fromBlockNumber,
      toBlockNumber,
    });
  }

  processTransaction(resource: Resource, transactionHash: string): Observable<void> {
    return this.http.post<void>(`${this.cfg.baseUrl}/api/${resource}/process-transaction`, { transactionHash });
  }

  /**
   * Fallback error handler that returns a default value
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning a default result
      return of(result as T);
    };
  }
}
